import {
  UserReport,
  roundToTwoDecimals,
  useDynamicUserReport
} from "./reportUtils";
import { Alert, CircularProgress } from "@mui/material";
import {
  DataGrid,
  GridColDef,
  GridEventListener,
  GridToolbar
} from "@mui/x-data-grid";
import { DataFreshness } from "./DataFreshness";
import { ReportLevel, ReportSelectionPrompt } from "./ReportSelectionPrompt";

interface DynamicUserReportProps {
  programId?: string;
  roleFilter: string;
  startDate?: string;
  endDate?: string;
  columns: GridColDef<UserReport>[];
  handleRowClick: GridEventListener<"rowClick"> | undefined;
}

export const DynamicUserReport = ({
  programId,
  startDate,
  endDate,
  columns,
  handleRowClick,
  roleFilter
}: DynamicUserReportProps) => {
  const {
    status: fetchReportStatus,
    error: fetchReportError,
    report
  } = useDynamicUserReport(programId, startDate, endDate, roleFilter);

  if (!programId) {
    return <ReportSelectionPrompt reportLevel={ReportLevel.Program} />;
  }

  if (fetchReportStatus === "idle") {
    return <div>Awaiting Data...</div>;
  }

  if (fetchReportStatus === "loading") {
    return <CircularProgress />;
  }

  if (fetchReportStatus === "failed") {
    return <Alert severity="error">{fetchReportError}</Alert>;
  }

  const formattedReport: UserReport[] = report
    .map((row) => ({
      ...row,
      id: "",
      programRole: undefined,
      programRoleString: row.programRole ?? "",
      completedAssessmentEvents: row.completedAssessmentEvents ?? 0,
      createdAssessmentEvents: row.createdAssessmentEvents ?? 0,
      assignedAssessmentEvents: row.assignedAssessmentEvents ?? 0,
      expiredAssessmentEvents: row.expiredAssessmentEvents ?? 0,
      percentCompleted: row.percentCompleted
        ? roundToTwoDecimals(row.percentCompleted * 100)
        : 0,
      percentExpired: row.percentExpired
        ? roundToTwoDecimals(row.percentExpired * 100)
        : 0,
      programMembershipId: "",
      userId: row.userId ?? ""
    }))
    .sort((a, b) => {
      const ln = (a.lastName ?? "").localeCompare(b.lastName ?? "");
      return ln !== 0 ? ln : (a.firstName ?? "").localeCompare(b.firstName ?? "");
    });
  return (
    <>
      <DataFreshness />
      <DataGrid
        aria-label="reports-table"
        rows={formattedReport}
        getRowId={(row) => `${row.userId ?? ""}-${row.programRole ?? ""}`}
        columns={columns}
        slots={{ toolbar: GridToolbar }}
        onRowClick={handleRowClick}
      />
    </>
  );
};
